/**
 * LandingPage — accueil « Encre & Papier ».
 *
 * Le produit d'abord : un mini-générateur (classe + notion) en guise de héros,
 * et la vraie fiche A4 à côté — la preuve avant le discours. Pas de tunnel
 * marketing : un visiteur peut commencer en un geste.
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useBeta } from "../hooks/useBeta";
import "../styles/sheet.css";
import "../styles/exercice.css";
import { porteOuverte, useOuvertureH } from "./fiche/ouverture";


const NIVEAUX = ["CP", "CE1", "CE2", "CM1", "CM2", "6e", "5e", "4e", "3e"];

/* Fiche de démonstration : contenu réellement produit par les générateurs 6e
   (fractions), dans le style exact du PDF — c'est l'objet que l'on vend. */
const Frac = ({ n, d }) => (
  <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", verticalAlign: "middle", lineHeight: 1.05, fontWeight: 600, margin: "0 2px" }}>
    <span style={{ borderBottom: "1.5px solid #1f2937", padding: "0 4px" }}>{n}</span>
    <span style={{ padding: "0 4px" }}>{d}</span>
  </span>
);

/**
 * Une feuille A4 mise à l'échelle.
 *
 * `.lmm-page` est, par construction, une page absolue de 794 × 1123 px : elle
 * DOIT vivre dans un `.lmm-sheet-frame` qui lui donne son échelle et réserve
 * sa place dans le flux. Utilisée nue, elle sortait du document et recouvrait
 * les sections suivantes de l'accueil. Le contenu est donc rendu aux
 * dimensions réelles du PDF puis réduit — exactement comme dans l'atelier :
 * ce que le visiteur voit est la fiche telle qu'elle s'imprimera.
 */
const A4Frame = ({ children, style }) => {
  const frameRef = useRef(null);
  const [scale, setScale] = useState(0.5);

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const apply = () => setScale(Math.min(1, (el.clientWidth || 794) / 794));
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={frameRef} className="lmm-sheet-frame" style={style}>
      <div className="lmm-page" style={{ "--lmm-scale": scale }}>{children}</div>
    </div>
  );
};

const HeroSheet = () => (
  <div className="relative mx-auto w-full max-w-[440px] pr-3 pt-3" aria-hidden="true">
    {/* le corrigé dépasse derrière la fiche */}
    <A4Frame style={{ position: "absolute", top: 0, right: 0, transform: "rotate(2.4deg)" }}>
      <div className="lmm-sheet-header">
        <div className="lmm-title">Fractions — Corrigé</div>
      </div>
      <div className="lmm-sol">1/2 = <strong>0,5 = 50 %</strong></div>
      <div className="lmm-sol">0,36 = <strong>36/100 = 9/25</strong></div>
    </A4Frame>
    <span className="absolute right-0 top-1 z-10 rounded-full bg-[#16a34a] px-3 py-1 text-xs font-bold text-white shadow-sm">
      + corrigé expliqué
    </span>
    <A4Frame>
      <div className="lmm-sheet-header">
        <div className="lmm-title">Fractions — Fiche d'exercices</div>
        <div className="lmm-meta"><span>6e</span><span>Nom : ................</span><span>Date : ....../....../......</span></div>
      </div>
      {[
        {
          t: "Fractions équivalentes",
          b: (<>Complète pour obtenir des fractions égales.<br />
            a. <Frac n="1" d="10" /> = <Frac n="?" d="60" /> &nbsp; b. <Frac n="1" d="5" /> = <Frac n="3" d="?" /> &nbsp; c. <Frac n="3" d="5" /> = <Frac n="?" d="10" /></>),
        },
        {
          t: "Fractions décimales et pourcentages",
          b: (<>Compléter (décimal et pourcentage) :<br /><Frac n="1" d="2" /> = ............ = ............ %</>),
        },
        {
          t: "Écriture décimale",
          b: (<>Écrire le nombre décimal sous forme de fraction décimale :<br /><b>0,36</b> = ............</>),
        },
        {
          t: "Addition et soustraction",
          b: (<>Calculer :<br />a. <Frac n="4" d="5" /> − <Frac n="3" d="5" /> &nbsp; b. <Frac n="2" d="9" /> + <Frac n="4" d="9" /></>),
        },
        {
          t: "Comparer des fractions",
          b: (<>Ranger dans l'ordre croissant :<br /><Frac n="3" d="4" /> ; <Frac n="1" d="4" /> ; <Frac n="5" d="4" /> ; <Frac n="2" d="4" /></>),
        },
        {
          t: "Problème",
          b: (<>Une classe de 25 élèves part en sortie. <Frac n="3" d="5" /> des élèves prennent
            le bus, les autres viennent à vélo. Combien d'élèves viennent à vélo ?</>),
        },
      ].map((ex, i) => (
        <div className="lmm-ex" key={ex.t}>
          <div className="lmm-ex-header">
            <span className="lmm-ex-num">{i + 1}</span>
            <span className="lmm-ex-title">{ex.t}</span>
          </div>
          <div className="lmm-ex-body">{ex.b}</div>
        </div>
      ))}
    </A4Frame>
  </div>
);

function LandingPage() {
  const beta = useBeta();
  const navigate = useNavigate();
  // L'état d'ouverture vient du SERVEUR ; le bundle ne peut que fermer plus
  // tôt. Voir `components/fiche/ouverture.js`.
  const ouvertureH = useOuvertureH();
  const [classe, setClasse] = useState("6e");
  const [notion, setNotion] = useState("");

  const start = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    params.set("classe", classe);
    if (notion.trim()) params.set("q", notion.trim());
    navigate(`/generer?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ===== HÉROS : le produit, tout de suite ===== */}
      <section className="mx-auto grid max-w-[1180px] items-center gap-12 px-5 pb-10 pt-12 md:grid-cols-[1.05fr_.95fr] md:gap-16 md:pt-16">
        <div>
          <p className="mb-4 text-xs font-bold uppercase tracking-[.14em] text-primary">
            Enseignants · Parents · Formateurs
          </p>
          <h1 className="mb-4 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-foreground md:text-5xl" style={{ textWrap: "balance" }}>
            La bonne fiche d'exercices. Prête en 30 secondes.
          </h1>
          <p className="mb-7 max-w-[54ch] text-[17px] text-muted-foreground">
            Choisissez la classe et la notion : Le Maître Mot compose une fiche
            de mathématiques imprimable, avec son corrigé expliqué. Du CP à la 3e,
            construite à partir des programmes officiels.
          </p>

          <form onSubmit={start}
            className="grid max-w-[620px] grid-cols-1 items-end gap-3 rounded-xl border border-border bg-card p-5 shadow-sm sm:grid-cols-[120px_1fr_auto]">
            <div>
              <Label htmlFor="home-classe" className="mb-1.5 block text-[13px] font-semibold">Classe</Label>
              <select id="home-classe" value={classe} onChange={(e) => setClasse(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-card px-3 text-[15px] text-foreground">
                {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="home-notion" className="mb-1.5 block text-[13px] font-semibold">Que doit travailler l'élève ?</Label>
              <Input id="home-notion" value={notion} onChange={(e) => setNotion(e.target.value)}
                placeholder="Ex : les fractions" className="h-12 bg-card text-[15px]" />
            </div>
            <Button type="submit" className="h-12 px-6 text-[15px]">Créer ma fiche</Button>
          </form>

          {/* ARCHITECTURE H — l'entrée qui permet de COMPARER les deux parcours
              sur le même contenu, en production, sans redéploiement.
              La route `/fiche` existe toujours, pour que les liens déjà
              partagés ne cassent pas quand on referme la porte.

              C'EST LE SERVEUR QUI DÉCIDE si cette porte s'affiche : lui seul
              sait si les routes répondent. `REACT_APP_FICHE_H=0` ne peut que
              la fermer plus tôt. L'accueil invitait jusqu'ici à « essayer le
              nouveau parcours » pendant que le serveur répondait 404. */}
          {porteOuverte(ouvertureH) && (
            <p className="mt-4 text-[14px]">
              <Link
                to="/fiche"
                className="font-semibold text-primary underline underline-offset-4"
              >
                Créer une fiche par objectif
              </Link>
              <span className="text-muted-foreground">
                {" "}— choisissez un objectif précis, la fiche est composée aussitôt.
              </span>
            </p>
          )}

          <p className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-[13px] text-muted-foreground">
            {["Gratuit, sans carte bancaire", "Fiche élève + corrigé expliqué", "Plus de 140 chapitres, du CP à la 3e"].map((r) => (
              <span key={r} className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className="font-bold text-primary">✓</span>{r}
              </span>
            ))}
          </p>
        </div>

        <HeroSheet />
      </section>

      {/* ===== 3 gestes ===== */}
      <section aria-label="Comment ça marche" className="mx-auto grid max-w-[1180px] gap-5 px-5 pb-16 pt-4 md:grid-cols-3">
        {[
          ["1", "Choisissez", "La classe, puis la notion à travailler — en toutes lettres, sans jargon."],
          ["2", "Ajustez", "Gardez, remplacez ou réordonnez les exercices, en voyant la fiche exacte."],
          ["3", "Imprimez", "La fiche élève et son corrigé expliqué, prêts à distribuer."],
        ].map(([k, t, d]) => (
          <div key={k} className="border-t-2 border-border pt-4">
            <p className="mb-1 text-[15px] font-semibold text-foreground">
              <span className="mr-2 font-serif font-bold text-primary">{k}.</span>{t}
            </p>
            <p className="text-sm text-muted-foreground">{d}</p>
          </div>
        ))}
      </section>

      {/* ===== honnêteté / offre — une ligne, pas un argumentaire ===== */}
      <section className="border-t border-border bg-card/60">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center gap-x-8 gap-y-3 px-5 py-8 text-sm text-muted-foreground">
          {/* Pendant la bêta, deux de ces trois phrases étaient fausses.
              « 3 fiches PDF par jour » SOUS-ESTIME ce qu'un compte reçoit
              maintenant — l'accès complet — et « Voir l'offre Pro » désigne une
              offre qui n'existe pas. Dire vrai est ici la version la plus
              généreuse : on annonce plus, pas moins. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/60 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
            {beta.active ? "Bêta Fondateur — premiers utilisateurs bienvenus"
                         : "Version beta — premiers utilisateurs bienvenus"}
          </span>
          <span>
            {beta.active
              ? `Accès complet offert pendant ${Math.round(beta.jours / 30)} mois, sans carte bancaire.`
              : "Gratuit : 3 fiches PDF par jour, avec un compte gratuit."}
          </span>
          <button type="button" onClick={() => navigate("/pricing")} className="font-semibold text-primary hover:underline">
            {beta.active ? "Découvrir la bêta →" : "Voir l'offre Pro →"}
          </button>
          <a href="mailto:contact@wana-family.fr" className="ml-auto hover:text-foreground hover:underline">
            Un bug, une idée ? contact@wana-family.fr
          </a>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
